/**
 * RichTextEditor - Lightweight rich text editor for section descriptions
 *
 * Supports basic formatting: bold, italic, underline, and bullet lists.
 * Uses contentEditable for a lightweight implementation without heavy dependencies.
 */
import { useRef, useCallback, useEffect, memo, useState } from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Divider from '@mui/material/Divider';
import { alpha } from '@mui/material/styles';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import LinkIcon from '@mui/icons-material/Link';
import { useBuilderTranslation } from '@/i18n/hooks/useTypedTranslation';
import { LinkModal } from './LinkModal';

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  placeholder?: string;
  minHeight?: number;
}

export const RichTextEditor = memo(function RichTextEditor({
  value,
  onChange,
  onBlur,
  placeholder = 'Add description...',
  minHeight = 60,
}: RichTextEditorProps) {
  const { t } = useBuilderTranslation();
  const editorRef = useRef<HTMLDivElement>(null);
  const isInternalChange = useRef(false);
  const editingLinkRef = useRef<HTMLAnchorElement | null>(null);
  const savedRangeRef = useRef<Range | null>(null);
  const [activeFormats, setActiveFormats] = useState<Set<string>>(new Set());
  const [linkModalOpen, setLinkModalOpen] = useState(false);
  const [selectedText, setSelectedText] = useState('');
  const [selectedUrl, setSelectedUrl] = useState('');

  // Update active formats based on selection
  const updateActiveFormats = useCallback(() => {
    const formats = new Set<string>();
    if (document.queryCommandState('bold')) formats.add('bold');
    if (document.queryCommandState('italic')) formats.add('italic');
    if (document.queryCommandState('underline')) formats.add('underline');
    if (document.queryCommandState('insertUnorderedList')) formats.add('bulletList');
    if (document.queryCommandState('insertOrderedList')) formats.add('numberedList');
    setActiveFormats(formats);
  }, []);

  // Sync external value changes to editor
  useEffect(() => {
    if (editorRef.current && !isInternalChange.current) {
      if (editorRef.current.innerHTML !== value) {
        editorRef.current.innerHTML = value;
      }
    }
    isInternalChange.current = false;
  }, [value]);

  // Handle content changes
  const handleInput = useCallback(() => {
    if (editorRef.current) {
      isInternalChange.current = true;
      onChange(editorRef.current.innerHTML);
    }
  }, [onChange]);

  // Apply formatting command
  const execCommand = useCallback((command: string, value?: string) => {
    editorRef.current?.focus();
    document.execCommand(command, false, value);
    handleInput();
    updateActiveFormats();
  }, [handleInput, updateActiveFormats]);

  // Format button click handler
  const handleFormat = useCallback((format: string) => (e: React.MouseEvent) => {
    e.preventDefault(); // Prevent focus loss
    switch (format) {
      case 'bold':
        execCommand('bold');
        break;
      case 'italic':
        execCommand('italic');
        break;
      case 'underline':
        execCommand('underline');
        break;
      case 'bulletList':
        execCommand('insertUnorderedList');
        break;
      case 'numberedList':
        execCommand('insertOrderedList');
        break;
      case 'link': {
        // Capture selected text and range for new link
        const selection = window.getSelection();
        const text = selection?.toString() || '';

        // Save the current selection range so we can restore it later
        if (selection && selection.rangeCount > 0) {
          savedRangeRef.current = selection.getRangeAt(0).cloneRange();
        }

        setSelectedText(text);
        setSelectedUrl(''); // Clear URL for new link
        editingLinkRef.current = null; // Not editing existing link
        setLinkModalOpen(true);
        break;
      }
    }
  }, [execCommand, t]);

  // Handle editor click to detect link clicks for editing
  const handleEditorClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;

    // Check if clicked element is a link or inside a link
    const linkElement = target.closest('a') as HTMLAnchorElement | null;

    if (linkElement) {
      e.preventDefault(); // Prevent link navigation
      // Extract current link text and URL
      const linkText = linkElement.textContent || '';
      const linkUrl = linkElement.getAttribute('href') || '';

      // Store reference to the link being edited
      editingLinkRef.current = linkElement;

      // Open modal with current link values
      setSelectedText(linkText);
      setSelectedUrl(linkUrl);
      setLinkModalOpen(true);
    }
  }, []);

  // Handle link submission from modal
  const handleLinkSubmit = useCallback((text: string, url: string) => {
    if (editorRef.current) {
      // Create the HTML link
      const link = `<a href="${url}" target="_blank" rel="noopener noreferrer">${text}</a>`;

      if (editingLinkRef.current) {
        // Editing existing link - replace it
        const range = document.createRange();
        range.selectNodeContents(editingLinkRef.current);
        const selection = window.getSelection();
        selection?.removeAllRanges();
        selection?.addRange(range);
        document.execCommand('insertHTML', false, link);
        editingLinkRef.current = null;
      } else {
        // Inserting new link - restore saved selection range
        editorRef.current.focus();

        if (savedRangeRef.current) {
          // Restore the saved selection
          const selection = window.getSelection();
          selection?.removeAllRanges();
          selection?.addRange(savedRangeRef.current);
          savedRangeRef.current = null; // Clear after use
        }

        document.execCommand('insertHTML', false, link);
      }

      handleInput();
      setSelectedText('');
      setSelectedUrl('');
    }
  }, [handleInput]);

  // Handle selection change to update active formats
  const handleSelectionChange = useCallback(() => {
    updateActiveFormats();
  }, [updateActiveFormats]);

  // Add selection change listener
  useEffect(() => {
    document.addEventListener('selectionchange', handleSelectionChange);
    return () => {
      document.removeEventListener('selectionchange', handleSelectionChange);
    };
  }, [handleSelectionChange]);

  const toolbarButtons = [
    { format: 'bold', icon: FormatBoldIcon, tooltip: t('richText.bold', 'Bold') },
    { format: 'italic', icon: FormatItalicIcon, tooltip: t('richText.italic', 'Italic') },
    { format: 'underline', icon: FormatUnderlinedIcon, tooltip: t('richText.underline', 'Underline') },
  ];

  const listButtons = [
    { format: 'bulletList', icon: FormatListBulletedIcon, tooltip: t('richText.bulletList', 'Bullet list') },
    { format: 'numberedList', icon: FormatListNumberedIcon, tooltip: t('richText.numberedList', 'Numbered list') },
  ];

  return (
    <Box sx={{ width: '100%' }}>
      {/* Formatting Toolbar */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.25,
          p: 0.5,
          mb: 0.5,
          bgcolor: alpha('#f1f5f9', 0.5),
          borderRadius: 1,
          border: 1,
          borderColor: 'divider',
          borderBottom: 'none',
          borderBottomLeftRadius: 0,
          borderBottomRightRadius: 0,
        }}
      >
        {toolbarButtons.map(({ format, icon: Icon, tooltip }) => (
          <Tooltip key={format} title={tooltip} placement="top">
            <IconButton
              size="small"
              onMouseDown={handleFormat(format)}
              sx={{
                p: 0.5,
                borderRadius: 0.5,
                color: activeFormats.has(format) ? 'primary.main' : 'text.secondary',
                bgcolor: activeFormats.has(format) ? alpha('#14B8A6', 0.1) : 'transparent',
                '&:hover': {
                  bgcolor: alpha('#14B8A6', 0.1),
                },
              }}
            >
              <Icon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        ))}

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.25 }} />

        {listButtons.map(({ format, icon: Icon, tooltip }) => (
          <Tooltip key={format} title={tooltip} placement="top">
            <IconButton
              size="small"
              onMouseDown={handleFormat(format)}
              sx={{
                p: 0.5,
                borderRadius: 0.5,
                color: activeFormats.has(format) ? 'primary.main' : 'text.secondary',
                bgcolor: activeFormats.has(format) ? alpha('#14B8A6', 0.1) : 'transparent',
                '&:hover': {
                  bgcolor: alpha('#14B8A6', 0.1),
                },
              }}
            >
              <Icon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        ))}

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.25 }} />

        <Tooltip title={t('richText.link', 'Add link')} placement="top">
          <IconButton
            size="small"
            onMouseDown={handleFormat('link')}
            sx={{
              p: 0.5,
              borderRadius: 0.5,
              color: 'text.secondary',
              '&:hover': {
                bgcolor: alpha('#14B8A6', 0.1),
              },
            }}
          >
            <LinkIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Editor Area */}
      <Box
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onInput={handleInput}
        onBlur={onBlur}
        onClick={handleEditorClick}
        data-placeholder={placeholder}
        sx={{
          minHeight,
          p: 1.5,
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          borderTopLeftRadius: 0,
          borderTopRightRadius: 0,
          bgcolor: 'background.paper',
          fontSize: '0.95rem',
          lineHeight: 1.6,
          color: 'text.primary',
          outline: 'none',
          transition: 'border-color 0.2s, box-shadow 0.2s',
          '&:focus': {
            borderColor: 'primary.main',
            boxShadow: (theme) => `0 0 0 2px ${alpha(theme.palette.primary.main, 0.1)}`,
          },
          '&:empty:before': {
            content: 'attr(data-placeholder)',
            color: 'text.disabled',
            pointerEvents: 'none',
          },
          // Style lists - ensure bullets/numbers are visible
          '& ul': {
            pl: 3,
            my: 0.5,
            listStyleType: 'disc',
          },
          '& ol': {
            pl: 3,
            my: 0.5,
            listStyleType: 'decimal',
          },
          '& li': {
            mb: 0.25,
            display: 'list-item',
          },
          // Style links - clickable to edit
          '& a': {
            color: 'primary.main',
            textDecoration: 'underline',
            cursor: 'pointer',
            '&:hover': {
              backgroundColor: alpha('#14B8A6', 0.1),
              borderRadius: '2px',
            },
          },
        }}
      />

      {/* Link Modal */}
      <LinkModal
        open={linkModalOpen}
        onClose={() => {
          setLinkModalOpen(false);
          setSelectedText('');
          setSelectedUrl('');
          editingLinkRef.current = null;
          savedRangeRef.current = null; // Clear saved range on cancel
        }}
        onSubmit={handleLinkSubmit}
        initialText={selectedText}
        initialUrl={selectedUrl}
      />
    </Box>
  );
});

export default RichTextEditor;
